// Shared helper for due-date status badge.
//
// Returns one of:
//   "done"     — task is completed; no badge should render.
//   "late"     — task is incomplete AND today > due_date.
//   "on_time"  — task is incomplete AND today <= due_date.
//   null       — task has no due_date; no badge.
//
// All comparisons are done on date strings (YYYY-MM-DD), which sorts
// lexicographically the same as chronologically — no Date() parsing,
// no timezone surprises.

export function todayIsoLocal() {
  // Use local-time day so a user in Chicago at 11:55pm doesn't see "late"
  // because UTC has already rolled over.
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function dueDateStatus(task, today = todayIsoLocal()) {
  if (!task) return null;
  if (task.kanban_status === "done") return "done";
  const due = (task.due_date || "").trim();
  if (!due) return null;
  return today > due ? "late" : "on_time";
}

// At-Risk predicate — must match the backend rule in
// `digest_engine.compute_at_risk`:
//   • Task is NOT done (kanban_status != "done"), AND
//   • due_date is set, AND
//   • due_date <= today + 2 days (i.e. late or due within next 48h)
export function isAtRisk(task, today = todayIsoLocal()) {
  if (!task) return false;
  if (task.kanban_status === "done") return false;
  const due = (task.due_date || "").trim();
  if (!due) return false;
  // soonIso = today + 2 days. Add as ISO so we avoid Date() / timezones.
  const [y, m, d] = today.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + 2);
  const soonIso = t.toISOString().slice(0, 10);
  return due <= soonIso;
}

// How many days of breathing room a task has — used for the "Cleared from
// At-Risk · N days buffer" toast. Returns null if no due_date set or task
// is done.
export function dueBufferDays(task, today = todayIsoLocal()) {
  if (!task) return null;
  if (task.kanban_status === "done") return null;
  const due = (task.due_date || "").trim();
  if (!due) return null;
  const [y1, m1, d1] = today.split("-").map(Number);
  const [y2, m2, d2] = due.split("-").map(Number);
  const a = Date.UTC(y1, m1 - 1, d1);
  const b = Date.UTC(y2, m2 - 1, d2);
  return Math.round((b - a) / 86400000);
}

// OVERSHOOT — task's computed end walks past its target due_date. Purely
// visual signal; the scheduling math doesn't move the task. Complements the
// late/on_time badge which compares TODAY vs due_date.
//
// Returns { overshoot: bool, days: number } — days is 0 when not in overshoot,
// positive otherwise (e.g. end = due_date + 3 -> days = 3).
export function overshootStatus(task) {
  if (!task) return { overshoot: false, days: 0 };
  if (task.kanban_status === "done") return { overshoot: false, days: 0 };
  const due = (task.due_date || "").trim();
  const end = (task.end || "").trim();
  if (!due || !end) return { overshoot: false, days: 0 };
  if (end <= due) return { overshoot: false, days: 0 };
  const [ey, em, ed] = end.split("-").map(Number);
  const [dy, dm, dd] = due.split("-").map(Number);
  const eMs = Date.UTC(ey, em - 1, ed);
  const dMs = Date.UTC(dy, dm - 1, dd);
  return { overshoot: true, days: Math.round((eMs - dMs) / 86400000) };
}
